import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import CustomSpan from 'components/common/text/CustomSpan';
import { EmployeeActions } from 'store/actionCreators';
import BarChart from 'components/common/chart/BarChart';
import { useShallowAppSelector } from 'store/hooks';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import DateConverter from 'components/common/convert/DateConverter';
import { useTranslation } from 'react-i18next';
import { PageSubTitle } from 'components/base/title/PageTitle';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';
import 'moment/locale/ko';
import 'moment/locale/ja';

interface SalesPointInfoProps {}

interface IProjectGraph {
  groupDate: string;
  totalCount: number;
}

function SalesPointContainer({}: SalesPointInfoProps) {
  const {
    language,
    fetchPrjectGraph: { todayProjectCount: toDayTotalProject, projectGraph: prjectGraphList },
    fetchPrjectGraphSuccess,
    fetchPrjectGraphPending,
    fetchPrjectGraphFailure,
  }: {
    language: string;
    fetchPrjectGraph: { todayProjectCount: number; projectGraph: IProjectGraph[] };
    fetchPrjectGraphSuccess: boolean;
    fetchPrjectGraphPending: boolean;
    fetchPrjectGraphFailure: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    fetchPrjectGraph: state.employee.prjectGraph.data || {},
    fetchPrjectGraphSuccess: state.employee.prjectGraph.success,
    fetchPrjectGraphPending: state.employee.prjectGraph.pending,
    fetchPrjectGraphFailure: state.employee.prjectGraph.failure,
  }));

  const { t } = useTranslation();
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);

  const [chartData, setChartData] = useState<object[] | []>([]);
  useEffect(() => {
    EmployeeActions.fetch_project_graph_request();
    return () => {
      setIsLoadFailure(false);
      setIsLoadSuccess(false);
    };
  }, []);

  const toDay: number = moment().unix();
  const [totalSalesData, setTotalSalesData] = useState<object[] | []>([]);

  const handleRefreshList = () => {
    EmployeeActions.fetch_project_graph_request();
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useDidUpdateEffect(() => {
    if (fetchPrjectGraphSuccess) {
      prjectGraphList?.forEach((item: IProjectGraph) => {
        const dateUnix = moment(item.groupDate).unix();
        const groupMonth = moment.unix(dateUnix).locale(language).format('MMM');
        const totalCount: number = item.totalCount;
        const groupData: { x: string; y: string; graphValue: string } = {
          x: groupMonth,
          y: `${totalCount}`,
          graphValue: `${totalCount}`,
        };
        setChartData((draft: object[]) => [...draft, { ...groupData }]);
      });
    }
  }, [fetchPrjectGraphSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPrjectGraphSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchPrjectGraphSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPrjectGraphFailure) return;
    setIsLoadFailure(false);
  }, [fetchPrjectGraphFailure]);

  useDidUpdateEffect(() => {
    if (chartData.length > 0) {
      const animation = setTimeout(() => setTotalSalesData(chartData), 1);
      return () => {
        clearTimeout(animation);
      };
    }
  }, [chartData]);

  return (
    <StyledSalesPoint data-component-name="SalesPoint">
      <div className="salesPoint__container">
        <PageSubTitle title={t('TITLE_SALES_POINT')} />
        <div className="salesPoint__content_wrapper">
          <div className="salesPoint__info_box">
            <div className="salesPoint__info_today">
              <CustomSpan fontSize={33} fontWeight={800} fontColor="#0782ED">
                <T>GLOBAL_TODAY</T>
              </CustomSpan>
              <CustomSpan fontSize={14} fontColor="#858997">
                {!!isLoadSuccess ? (
                  <DateConverter
                    language={language}
                    timestamp={toDay}
                    format={language === 'en' ? 'MM.DD.YY hh:mm' : 'YY/MM/DD hh:mm'}
                  />
                ) : (
                  <div className="emptyEl"></div>
                )}
              </CustomSpan>
            </div>
            <div className="salesPoint__info_project">
              <CustomSpan fontSize={52} fontWeight={800} fontColor="#303030">
                {!!isLoadSuccess ? toDayTotalProject : '0'}
              </CustomSpan>
              <CustomSpan fontSize={23} marginLeft={13} fontWeight={400} fontColor="#7C7C7C">
                <T>GLOBAL_PROJECTS</T>
              </CustomSpan>
            </div>
          </div>
          <div className="salesPoint__chart_wrapper">
            <div className="salesPoint__chart_title">
              <CustomSpan fontSize={19} fontWeight={800}>
                <T>GLOBAL_PROJECTS</T>
              </CustomSpan>
            </div>
            <div className="salesPoint__chart_box">
              {!isLoadSuccess && !!fetchPrjectGraphPending && (
                <div className="infinite-scroll-loading">
                  <CircularLoading />
                </div>
              )}
              {!!isLoadSuccess && (
                <BarChart
                  width="100%"
                  height={200}
                  data={totalSalesData}
                  padding={0.7}
                  colors={'#00A4E3'}
                  margin={{ top: 5, right: 20, bottom: 30, left: 40 }}
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 15,
                    tickRotation: 0,
                    legendPosition: 'middle',
                  }}
                  className="salesPoint__chart"
                />
              )}
              {!!isLoadFailure && (
                <ListEmpty
                  text="GLOBAL_LIST_ERROR"
                  isRefreshButton={true}
                  onRefresh={handleRefreshList}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledSalesPoint>
  );
}

const StyledSalesPoint = styled.section<{}>`
  .emptyEl {
    width: 100%;
    height: 14px;
  }
  .salesPoint__container {
    padding-bottom: 40px;
    flex-wrap: wrap;
    width: 466px;
    margin-bottom: 20px;

    .salesPoint__title_box {
      margin: 20px 0;
      display: flex;
      align-items: center;
      .salesPoint__title_icon {
        width: 60px;
        height: 60px;
      }
    }

    .salesPoint__content_wrapper {
      width: 100%;
      height: 448px;
      background-color: #ffffff;
      border-radius: 15px;
      box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.16);
      padding-top: 45px;
      padding-left: 30px;
      .salesPoint__info_box {
        display: flex;
        align-items: center;
        margin-bottom: 50px;
        .salesPoint__info_today {
          width: 140px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          flex-wrap: wrap;
          row-gap: 10px;
        }
        .salesPoint__info_project {
          display: flex;
          align-items: center;
        }
      }
      .salesPoint__chart_wrapper {
        .salesPoint__chart_title {
          margin-bottom: 45px;
        }
        .salesPoint__chart_box {
          width: calc(100% - 50px);
          height: 200px;
          position: relative;
        }
        .salesPoint__chart {
          background-color: transparent;
          margin-left: 0;
        }
      }
    }
  }
`;

export default React.memo(SalesPointContainer);
